/**
 * Mojo Up AI Cloud's organisation switcher, in the sidebar's header where Enterprise shows its one
 * organisation: the active organisation as one quiet control, and a popover with the others
 * (Personal first), each with what the person is there. "Create a team" makes one and switches to it.
 */

import type { CloudRole, OrganisationSummary } from '@aioe/shared';
import { Check, ChevronsExpandVertical, Person, Persons, Plus } from '@gravity-ui/icons';
import { Button, Input, Label, Popover, TextField } from '@heroui/react';
import { useState } from 'react';

import { problemText, routes } from '../../api/client.ts';
import { useCloud } from '../../auth/AuthProvider.tsx';
import { FormModal } from './FormModal.tsx';
import { Turnstile, useTurnstileSiteKey } from './Turnstile.tsx';

export const ROLE_TEXT: Record<CloudRole, string> = { owner: 'Owner', admin: 'Admin', member: 'Member', viewer: 'Viewer' };
const PLAN_TEXT = { free: 'Free', teams: 'Teams' } as const;

/** What a person is in an organisation, in a few words. */
export const organisationDetail = (o: OrganisationSummary) => (o.kind === 'personal' ? `${PLAN_TEXT[o.plan]} · yours alone` : `${PLAN_TEXT[o.plan]} team · ${ROLE_TEXT[o.role].toLowerCase()}`);

function OrganisationIcon({ organisation, className }: { organisation: OrganisationSummary; className?: string }) {
  return organisation.kind === 'personal' ? <Person className={className} /> : <Persons className={className} />;
}

export function OrganisationSwitcher() {
  const cloud = useCloud();
  const [open, setOpen] = useState(false);
  const [creating, setCreating] = useState(false);
  if (!cloud) return null;
  const { organisation: active, me, switchTo } = cloud;
  return (
    <>
      <Popover isOpen={open} onOpenChange={setOpen}>
        <Button variant="ghost" className="org-switcher" aria-label={`Organisation: ${active.name}. Switch organisation`} data-org-switcher>
          <img src="/mojoup-logo.svg" alt="" className="size-7 shrink-0 rounded-lg object-contain" draggable={false} />
          <span className="sidebar-collapse-hide min-w-0 flex-1 text-left">
            <span className="block truncate text-body font-semibold">{active.name}</span>
            <span className="block truncate text-2xs text-muted">Mojo Up AI Cloud · {PLAN_TEXT[active.plan]}</span>
          </span>
          <ChevronsExpandVertical className="sidebar-collapse-hide size-3.5 shrink-0 text-muted" />
        </Button>
        <Popover.Content placement="bottom start" className="w-72 max-w-[calc(100vw-1rem)]">
          <Popover.Dialog className="flex flex-col gap-1 p-2" aria-label="Switch organisation">
            <span className="pane-label px-2 pb-1 pt-1">Organisations</span>
            <ul className="flex flex-col gap-0.5" role="list">
              {me.organisations.map((o) => {
                const current = o.id === active.id;
                return (
                  <li key={o.id}>
                    <button
                      type="button"
                      className="org-switcher__item"
                      aria-current={current ? 'true' : undefined}
                      onClick={() => {
                        setOpen(false);
                        if (!current) switchTo(o.id);
                      }}
                    >
                      <span className="org-switcher__icon" data-kind={o.kind}>
                        <OrganisationIcon organisation={o} className="size-3.5" />
                      </span>
                      <span className="min-w-0 flex-1">
                        <span className="block truncate text-sm font-medium">{o.name}</span>
                        <span className="block truncate text-xs text-muted">{organisationDetail(o)}</span>
                      </span>
                      {current ? <Check className="size-4 shrink-0 text-accent" aria-label="Active" /> : null}
                    </button>
                  </li>
                );
              })}
            </ul>
            <button
              type="button"
              className="org-switcher__item mt-1"
              onClick={() => {
                setOpen(false);
                setCreating(true);
              }}
            >
              <span className="org-switcher__icon org-switcher__icon--add">
                <Plus className="size-3.5" />
              </span>
              <span className="text-sm">Create a team</span>
            </button>
          </Popover.Dialog>
        </Popover.Content>
      </Popover>
      <CreateTeamModal isOpen={creating} onClose={() => setCreating(false)} />
    </>
  );
}

/** "Create a team": a name, then the new team is the active organisation. */
export function CreateTeamModal({ isOpen, onClose }: { isOpen: boolean; onClose: () => void }) {
  const cloud = useCloud();
  const [name, setName] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string>();
  const siteKey = useTurnstileSiteKey();
  const [check, setCheck] = useState<string>();
  const [checkRound, setCheckRound] = useState(0);
  const close = () => {
    setName('');
    setError(undefined);
    onClose();
  };
  const create = async () => {
    setBusy(true);
    setError(undefined);
    try {
      const org = await routes.createOrganisation(name.trim(), check).finally(() => {
        // An answer is spent once: a fresh check for the next try.
        setCheck(undefined);
        setCheckRound((n) => n + 1);
      });
      await cloud?.reload();
      cloud?.switchTo(org.id);
      close();
    } catch (e) {
      setError(problemText(e));
    } finally {
      setBusy(false);
    }
  };
  return (
    <FormModal
      isOpen={isOpen}
      onClose={close}
      title="Create a team"
      blurb="A team shares workbenches, projects, policies and secrets. You will be its owner, and can invite people once it exists."
      submitLabel="Create team"
      canSubmit={name.trim().length > 0 && (!siteKey || Boolean(check))}
      busy={busy}
      error={error}
      onSubmit={() => void create()}
    >
      <TextField variant="secondary" value={name} onChange={setName} isRequired autoFocus>
        <Label>Team name</Label>
        <Input placeholder="Acme engineering" maxLength={100} />
      </TextField>
      {siteKey && isOpen ? <Turnstile key={checkRound} siteKey={siteKey} onToken={setCheck} /> : null}
    </FormModal>
  );
}
